import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage } from '@packages/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionButton } from '@/components/ActionButton';
import { ActionDropdown } from '@/components/ActionDropdown';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LinklyDeviceConnectionStatus } from './LinklyDeviceConnectionStatus';

export const Route = createFileRoute('/a/linkly/devices/')({
  staticData: {
    breadcrumbs: [{ label: 'Linkly Devices' }],
  },
  component: LinklyDeviceListPage,
});

function LinklyDeviceListPage() {
  const convex = useConvex();
  const { data: devices, isPending, isError, error } = useQuery(api.linklyDevices.listAll);
  const [deviceToDelete, setDeviceToDelete] = useState<{ id: string; label: string } | null>(null);

  const handleDelete = async () => {
    if (!deviceToDelete) return;
    try {
      await convex.mutation(api.linklyDevices.destroy, { linklyDeviceId: deviceToDelete.id });
      toast.success('Deleted successfully');
      setDeviceToDelete(null);
    } catch (error) {
      toast.error("Couldn't delete", {
        description: getUserErrorMessage(error),
      });
    }
  };

  if (isPending) {
    return <LoadingIcon className="mx-auto mt-8" />;
  }
  if (isError) return <ErrorAlert message={error.message} title="Error loading" />;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Linkly Devices</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Restaurant</TableHead>
                <TableHead>Device</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sessions</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {devices?.map(({ linklyDevice: device, restaurantName, connectionStatus }) => (
                <TableRow key={device._id}>
                  <TableCell>
                    {restaurantName ? (
                      <Link
                        to="/r/$restaurantId/s/general-settings"
                        params={{ restaurantId: device.restaurantId }}
                        className="hover:underline"
                      >
                        {restaurantName}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono">{device.posTerminalUuid ?? '-'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <LinklyDeviceConnectionStatus connectionStatus={connectionStatus} />
                  </TableCell>
                  <TableCell>
                    <Link
                      to="/a/linkly/devices/$linklyDeviceId"
                      params={{ linklyDeviceId: device._id }}
                      className="underline"
                    >
                      Sessions
                    </Link>
                  </TableCell>
                  <TableCell>
                    <ActionDropdown
                      onDelete={() =>
                        setDeviceToDelete({
                          id: device._id,
                          label: restaurantName ?? device.posTerminalUuid ?? device._id,
                        })
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
              {!devices?.length && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4">
                    No Linkly devices found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog
        open={!!deviceToDelete}
        onOpenChange={(open) => !open && setDeviceToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Linkly device?</AlertDialogTitle>
            <AlertDialogDescription>
              {deviceToDelete?.label} will be unpaired and removed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <ActionButton variant="destructive" onClick={handleDelete}>
              Delete
            </ActionButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
